// Plan 4 Task 4 — Category preset editor (create + edit).
//
// Authors a category's `name` + `presets`. Per the Task 4 scope decision,
// the editor authors ONLY:
//   - presets.defaultDestinations[] — MVP core fields (name, adapter,
//     exportFormatId) + a generated stable id. The operator finishes
//     routing/config on the job after creation.
//   - presets.defaultPipelineTemplate — a PIPELINE_TEMPLATES id.
// No roles control (P4-3 deferred — `defaultRoleIds` has no grant semantics
// yet, so a control would write a no-op field).
import { useState } from "react";
import type { CategoryPresets, ExportAdapterType } from "@capture/shared";
import { PIPELINE_TEMPLATES } from "@capture/shared/types";
import { ADAPTER_OPTIONS } from "@/features/admin/form-config/export/destination-options";
import { useExportFormats } from "@/features/admin/hooks/useExportFormats";
import type { JobTypeCategory, CategoryWriteBody } from "./useJobTypeCategories";
import styles from "./CategoryEditor.module.css";

/** MVP preset destination draft — the core fields an operator authors here. */
interface PresetDestinationDraft {
  /** Stable local id; also written into the assembled preset. */
  id: string;
  name: string;
  adapter: ExportAdapterType | "";
  exportFormatId: string;
}

export interface CategoryEditorProps {
  mode: "create" | "edit";
  initial?: JobTypeCategory;
  onClose: () => void;
  onSubmit: (body: CategoryWriteBody) => void;
}

function newDraftId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `dest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function presetsToDrafts(presets: CategoryPresets | undefined): PresetDestinationDraft[] {
  const dests = presets?.defaultDestinations ?? [];
  return dests.map((d) => ({
    id: d.id || newDraftId(),
    name: d.name ?? "",
    adapter: (d.adapter as ExportAdapterType) ?? "",
    exportFormatId: d.exportFormatId ?? "",
  }));
}

export function CategoryEditor({ mode, initial, onClose, onSubmit }: CategoryEditorProps) {
  const { formats } = useExportFormats();

  const [name, setName] = useState(initial?.name ?? "");
  const [displayOrder, setDisplayOrder] = useState<string>(
    initial?.displayOrder != null ? String(initial.displayOrder) : "",
  );
  const [templateId, setTemplateId] = useState<string>(
    initial?.presets?.defaultPipelineTemplate ?? "",
  );
  const [destinations, setDestinations] = useState<PresetDestinationDraft[]>(
    presetsToDrafts(initial?.presets),
  );

  const canSave = name.trim().length > 0;

  function addDestination() {
    setDestinations((prev) => [
      ...prev,
      { id: newDraftId(), name: "", adapter: "", exportFormatId: "" },
    ]);
  }

  function updateDestination(id: string, patch: Partial<PresetDestinationDraft>) {
    setDestinations((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    );
  }

  function removeDestination(id: string) {
    setDestinations((prev) => prev.filter((d) => d.id !== id));
  }

  function handleSubmit() {
    if (!canSave) return;

    // Drop added-but-blank destination rows — a row with no name OR no adapter
    // is incomplete and must not be serialized into the presets (otherwise it
    // ships an `adapter: ""` cast as ExportAdapterType).
    const presets: CategoryPresets = {
      defaultDestinations: destinations
        .filter((d) => d.name.trim().length > 0 && d.adapter.trim().length > 0)
        .map((d) => ({
          id: d.id,
          name: d.name.trim(),
          adapter: d.adapter as ExportAdapterType,
          exportFormatId: d.exportFormatId || null,
          // MVP: operator completes format/config on the job after creation.
        })) as CategoryPresets["defaultDestinations"],
    };
    if (templateId) presets.defaultPipelineTemplate = templateId;

    const body: CategoryWriteBody = { name: name.trim(), presets };
    const orderN = Number.parseInt(displayOrder, 10);
    if (Number.isFinite(orderN)) body.displayOrder = orderN;

    onSubmit(body);
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2>{mode === "create" ? "Create category" : "Edit category"}</h2>

        <div className={styles.field}>
          <label htmlFor="category-name">
            <span>Name</span>
          </label>
          <input
            id="category-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Invoices"
            autoFocus
            aria-label="name"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="category-order">
            <span>Display order</span>
          </label>
          <input
            id="category-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            placeholder="0"
            aria-label="display order"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="category-template">
            <span>Pipeline template</span>
          </label>
          <select
            id="category-template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            aria-label="pipeline template"
          >
            <option value="">— None —</option>
            {Object.values(PIPELINE_TEMPLATES).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <small className={styles.help}>
            New jobs created in this category start from this pipeline.
          </small>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Default export destinations</span>
            <button type="button" className={styles.secondary} onClick={addDestination}>
              + Add destination
            </button>
          </div>

          {destinations.length === 0 ? (
            <p className={styles.help}>
              No default destinations. New jobs in this category start with none.
            </p>
          ) : (
            <ul className={styles.destList}>
              {destinations.map((d) => (
                <li key={d.id} className={styles.destRow}>
                  <div className={styles.destField}>
                    <label htmlFor={`dest-name-${d.id}`}>
                      <span>Name</span>
                    </label>
                    <input
                      id={`dest-name-${d.id}`}
                      value={d.name}
                      onChange={(e) => updateDestination(d.id, { name: e.target.value })}
                      placeholder="Destination name"
                      aria-label="destination name"
                    />
                  </div>
                  <div className={styles.destField}>
                    <label htmlFor={`dest-adapter-${d.id}`}>
                      <span>Adapter</span>
                    </label>
                    <select
                      id={`dest-adapter-${d.id}`}
                      value={d.adapter}
                      onChange={(e) =>
                        updateDestination(d.id, {
                          adapter: e.target.value as ExportAdapterType,
                        })
                      }
                      aria-label="destination adapter"
                    >
                      <option value="">— Select —</option>
                      {ADAPTER_OPTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.destField}>
                    <label htmlFor={`dest-format-${d.id}`}>
                      <span>Format</span>
                    </label>
                    <select
                      id={`dest-format-${d.id}`}
                      value={d.exportFormatId}
                      onChange={(e) =>
                        updateDestination(d.id, { exportFormatId: e.target.value })
                      }
                      aria-label="destination format"
                    >
                      <option value="">— None —</option>
                      {formats.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removeDestination(d.id)}
                    aria-label="remove destination"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={!canSave}
            onClick={handleSubmit}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
